import React from "react";
import { useLocalStorage } from "./useLocalStorage";
import { jwtDecode } from "jwt-decode";

type ReactNode =
  | React.ReactNode
  | React.ReactPortal
  | boolean
  | null
  | undefined;

interface UserType {
  userId: string;
  username: string;
  roles: string;
  /**
   * What the login token actually carries (services/user.ts signs
   * `role: { role_id, role_name }`). `roles` above never matched the payload
   * and nothing read it; this is the field the server checks against.
   */
  role?: { role_id: string; role_name: string };
  exp: number;
  iat: number;
}

interface FeatureType {
  feature_id: string;
  feature_name: string;
  create_access: boolean;
  read_access: boolean;
  update_access: boolean;
  delete_access: boolean;
  is_active: boolean;
}

interface AuthContextType {
  token: string;
  user: UserType;
  features: FeatureType[];
  hasFeature: (name: string) => boolean;
  canWrite: (name: string) => boolean;
  canCreate: (name: string) => boolean;
  canUpdate: (name: string) => boolean;
  canDelete: (name: string) => boolean;
  /**
   * Role check, deliberately not a feature check.
   *
   * Used for actions the server restricts by ROLE rather than by a feature flag,
   * because flags get copied between roles as they are created. It only decides
   * what to SHOW — the server re-checks the same role and is the real gate.
   */
  isSuperAdmin: () => boolean;
  logIn: (token: string, redirect?: string) => boolean;
  logOut: (redirect?: string) => boolean;
  isValidUser: () => boolean;
}

const AuthContext = React.createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authToken, setAuthToken] = useLocalStorage("token", "");
  const [user, setUser] = useLocalStorage("user", "");
  const [features, setFeatures] = useLocalStorage("features", "");

  const providedValues = React.useMemo(() => {
    try {
      const user: UserType = jwtDecode(authToken);
      return {
        token: authToken,
        user,
      };
    } catch (err) {
      return { token: "", user: {} as UserType };
    }
  }, [authToken]);

  const hasFeature = (name: string) =>
    parsedFeatures.some((f) => f.feature_name === name && f.read_access);

  const canWrite = (name: string) =>
    parsedFeatures.some(
      (f) => f.feature_name === name && (f.update_access || f.create_access)
    );

  const canCreate = (name: string) =>
    parsedFeatures.some(
      (f) => f.feature_name === name && f.create_access
    );

  const canUpdate = (name: string) =>
    parsedFeatures.some(
      (f) => f.feature_name === name && f.update_access
    );

  const canDelete = (name: string) =>
    parsedFeatures.some(
      (f) => f.feature_name === name && f.delete_access
    );

  const logIn = (token: string, redirect?: string) => {
    let decoded: UserType & { features?: FeatureType[] };
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      return false;
    }

    setAuthToken(token);
    setUser(JSON.stringify(decoded));

    // If features are embedded in the token payload
    if (decoded.features && Array.isArray(decoded.features)) {
      setFeatures(JSON.stringify(decoded.features));
    }

    // Alternatively, you can call a /me or /features API after login and store the response

    if (redirect) {
      setTimeout(() => {
        window.location.replace(redirect);
      }, 500);
    }
    return true;
  };

  const logOut = (redirect?: string) => {
    setAuthToken("");
    localStorage.removeItem("token");

    setUser("");
    localStorage.removeItem("user");

    setFeatures("");
    localStorage.removeItem("features");

    if (redirect) {
      setTimeout(() => {
        window.location.replace(redirect);
      }, 500);
    }
    return true;
  };

  const isValidUser = () => {
    if (providedValues.token && providedValues.user) {
      if (!providedValues.user.userId || !providedValues.user.exp) return false;
      const expDate = new Date(providedValues.user.exp * 1000);
      const curDate = new Date(Date.now());

      return expDate > curDate;
    }
    return false;
  };

  // Matches the server's check exactly (partnerPortalResolver.requireAdmin):
  // role_name === "SUPER_ADMIN". Kept as one helper so the two cannot drift into
  // showing a control the server would then refuse.
  const isSuperAdmin = () => providedValues.user?.role?.role_name === "SUPER_ADMIN";

  const providedFunctions = { logIn, logOut, isValidUser, canDelete, isSuperAdmin };

  const parsedFeatures: FeatureType[] = React.useMemo(() => {
    if (typeof features === "string") {
      try {
        return JSON.parse(features) as FeatureType[];
      } catch {
        return [];
      }
    }
    return Array.isArray(features) ? features : [];
  }, [features]);

  const authContext: AuthContextType = {
    ...providedValues,
    features: parsedFeatures,
    hasFeature,
    canWrite,
    canCreate,
    canUpdate,
    ...providedFunctions,
  };

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return React.useContext(AuthContext as React.Context<AuthContextType>);
};
